/**
 * PATH: packages/drafting/src/ir/defaults.ts
 * FILE_ID: DRAFTING-IR-DEFAULTS-001
 * PURPOSE: Default layer palette + empty-drawing factory.
 * WHY: The colour-coded legend (Concrete/Aluminium/Glazing/Gyprock/
 *   Cut line/Annotation) is the UX north-star from the window-detail
 *   markup prototype; a fresh drawing starts with it so "color-coding
 *   like the generated HTML markup" works out of the box.
 */

import { IR_VERSION, type DrawingIR, type Layer } from './schema'

export const DEFAULT_LAYERS: Layer[] = [
  { id: 'concrete', name: 'Concrete', color: '#e9e6dd', stroke: '#2c3a47', visible: true },
  { id: 'aluminium', name: 'Aluminium', color: '#9aa6b2', stroke: '#2c3a47', visible: true },
  { id: 'glazing', name: 'Glazing', color: '#cfe7f2', stroke: '#2c3a47', visible: true },
  { id: 'gyprock', name: 'Gyprock', color: '#f3eef7', stroke: '#2c3a47', visible: true },
  { id: 'cutline', name: 'Cut line', color: 'none', stroke: '#d2362f', dash: [8, 5], visible: true },
  { id: 'annotation', name: 'Annotation', color: '#fff4a8', stroke: '#e3c93b', visible: true },
]

export interface CreateDrawingOptions {
  title?: string
  units?: DrawingIR['meta']['units']
  scaleRatio?: string
  paper?: DrawingIR['meta']['paper']
  gridSize?: number
}

/**
 * createEmptyDrawing — f(options) -> a valid empty DrawingIR.
 * Pure: no side effects, deterministic given inputs.
 */
export function createEmptyDrawing(opts: CreateDrawingOptions = {}): DrawingIR {
  return {
    ir_version: IR_VERSION,
    meta: {
      title: opts.title ?? 'Untitled drawing',
      units: opts.units ?? 'mm',
      scale: { ratio: opts.scaleRatio ?? '1:10' },
      paper: opts.paper ?? 'A3',
      grid: { size: opts.gridSize ?? 10, snap: true },
      origin: [0, 0],
    },
    layers: DEFAULT_LAYERS.map((l) => ({ ...l })),
    entities: [],
  }
}

/** Generate a short unique entity id (stable enough for a single session). */
export function makeEntityId(prefix = 'e'): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
}
